import { SIMPLMenuHeader } from "../../components/commons/header/SIMPLMenuHeader";
import { SIMPLTable } from "../../components/commons/table/SimplTable";
import { Layout } from "../../components/commons/layout/Layout";
import { LABELS } from "~/resources/Labels";
import { ChangeEvent, useEffect, useState } from "react";
import { PropRow, SortTable, TemplateResponse } from "~/models/Commons ";

import AddIcon from "@mui/icons-material/Add";
import { Entries } from "~/models/enums/Entries";
import {
  attachResourcesToCloudEnviroments,
  displayEntries,
  getDefaultQueryParams,
  sortScripts,
} from "~/Utils/Utils";
import { SIMPLButtonCustomized } from "../../components/commons/button/SIMPLButtonCustumized";
import { SIMPLAlert } from "../../components/commons/dialog/SimplAlert";
import { PayloadType } from "~/models/types/Payload";
import useSessionTimeout from "~/services/session/SessionTimeOut";
import { VmTemplateHeader } from "~/models/vmtemplate/VmTemplate";
import {
  useCopyVmTemplateMutation,
  useDeleteVmTemplatesMutation,
  useGetVmTemplatesQuery,
} from "~/services/vm/VmTemplate";
import { useGetComponentsQuery } from "~/services/vm/Components";
import { LoadingWrapper } from "../../components/commons/spiner/SimpleLoaderWrapper";
import { DetailsVmTemplate } from "./DetailsVmTemplate";
import { useLocation, useNavigate } from "react-router-dom";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { handleCopyEntity } from "~/Utils/helpers/CopyEntityHelper";
import { Alerts } from "../messages/Alerts";
import { handleDeleteEntity } from "~/Utils/helpers/DeleteEntityHelper";
import { useTranslation } from "react-i18next";

export const SIMPLVmTemplate = () => {
  const { t } = useTranslation();
  type ResponseDetailsVmTemplate = {
    title: string;
    message: string;
  };
  const [responseDetailsVmTemplate, setResponseDetailsVmTemplate] =
    useState<ResponseDetailsVmTemplate | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { success, response, error } = location?.state || {};
  const [deleteVmTemplate] = useDeleteVmTemplatesMutation();
  const [copyVmTemplate] = useCopyVmTemplateMutation();
  const [copyModalVmSuccess, setCopyModalVmSuccess] = useState(false);
  const [copyModalVmError, setCopyModalVmError] = useState(false);
  const { data: ConfigurationComponentsData } = useGetComponentsQuery(
    getDefaultQueryParams("vm-configuration"),
  );

  const { data: ProvissioningComponentsData } = useGetComponentsQuery(
    getDefaultQueryParams("post-provisioning-operations"),
  );

  const { data: PolicyComponentsData } = useGetComponentsQuery(
    getDefaultQueryParams("policies"),
  );

  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [viewModalVmTemplate, setViewModalVmTemplate] = useState(false);
  const [rows, setRows] = useState([] as PropRow[]);
  const [size, setSize] = useState<number>(5);
  const [page, setPage] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [showPagination, setShowPagination] = useState(true);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [item, setItem] = useState({} as TemplateResponse);
  const [deleteModal, setDeleteModal] = useState(false);
  const [copyModal, setCopyModal] = useState(false);
  const INACTIVITY_LIMIT = Number(import.meta.env.VITE_INACTIVITY_LIMIT);
  const FORCED_LOGOUT_LIMIT_EXPIRED_TOKEN = Number(
    import.meta.env.VITE_FORCED_LOGOUT_LIMIT_EXPIRED_TOKEN,
  );
  const [isActive, setIsActive] = useState(true);
  const { data, refetch, isLoading, isFetching, isError } =
    useGetVmTemplatesQuery({
      page: 0,
      size: 999,
      active: isActive,
    });
  const [sortConfig, setSortConfig] = useState<SortTable>({
    key: "",
    direction: LABELS.ASC,
  });
  const handleSort = (key: string) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === LABELS.ASC
        ? LABELS.Desc
        : LABELS.ASC;
    setSortConfig({ key, direction });
  };

  /**
   * Initializes session timeout handling.
   *
   * @param {Object} config - Configuration for session timeout.
   * @param {number} config.inactivityLimit - Time in ms before user is considered inactive.
   * @param {number} config.forcedLogoutLimit - Time in ms before forced logout after inactivity.
   * @param {Function} config.buildLogoutUrl - Function that returns the logout URL.
   * @param {Storage} config.storage - Storage mechanism (e.g., sessionStorage).
   * */
  useSessionTimeout({
    inactivityLimit: INACTIVITY_LIMIT,
    forcedLogoutLimit: FORCED_LOGOUT_LIMIT_EXPIRED_TOKEN,
    storage: sessionStorage,
  });

  /**
   * Initializes and sets the table rows by:
   * 1. Filtering scripts based on the active tab.
   * 2. Sorting the filtered scripts according to the sort configuration.
   * 3. Mapping sorted scripts to table row format and setting rows.
   */
  const initVmTemplate = () => {
    // Ensure payload exists before processing
    const payload = data?.payload as PayloadType | undefined;

    if (!payload || payload.length === 0) {
      setShowPagination(false);
      return;
    }
    // Attach resources, sort scripts, and customize entries
    const scriptMergedList = attachResourcesToCloudEnviroments(payload);
    const sortedScripts = sortScripts(scriptMergedList, sortConfig);
    const customizedList = displayEntries(sortedScripts, totalElements, 999);
    // Set pagination visibility and total elements count
    const totalPages = payload?.page?.totalPages ?? 0;
    setTotal(Number(totalPages));

    if (payload?.page?.totalElements) {
      setTotalElements(payload?.page?.totalElements);
    }

    // Build templateRows using spread
    const templateRows: PropRow[] = customizedList.map((row) => ({ row }));

    setRows([...templateRows]);

    setShowPagination(true);
  };
  const handleVmTemplateResponse = () => {
    if (success) {
      setResponseDetailsVmTemplate({
        title: t("MessageOperationCompletedSuceesuflly"),
        message: response?.message,
      });

      setCopyModalVmSuccess(true);
      setCopyModalVmError(false);
    } else if (error) {
      setResponseDetailsVmTemplate({
        title: `${error?.data?.title} (${error?.data?.status})`,
        message: error?.data?.detail,
      });

      setCopyModalVmSuccess(false);
      setCopyModalVmError(true);
    }
  };
  useEffect(() => {
    if (location.state) {
      handleVmTemplateResponse();
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state]);
  useEffect(() => {
    initVmTemplate();
  }, [
    data,
    true,
    sortConfig,
    size,
    total,
    totalElements,
    ConfigurationComponentsData,
    ProvissioningComponentsData,
    PolicyComponentsData,
    isActive,
  ]);

  /**
   * @effect
   * - Runs when the 'active' dependency changes.
   * - Calls setPage(1) to reset the pagination.
   */
  useEffect(() => {
    setPage(0);
    handlePaginationSize();
  }, [true, size]);
  const handlePaginationSize = () => {
    if ([Entries.Ten, Entries.Twenty].includes(size)) {
      setPage(0);
    }
  };

  /*Renders copy alert with cancel and delete actions */
  const renderCopyAlert = () =>
    copyModal && (
      <SIMPLAlert
        onCancel={() => setCopyModal(false)}
        message={t('MessageDialogCopy', { item: item.name })}
        title={t("CopyVmTemplate")}
        actions={renderCopyAlertButtons()}
      />
    );
  /* Renders "Cancel" and "Copy Item" buttons for alerts */
  const renderCopyAlertButtons = () => (
    <>
      <div className="grow-0 basis-32 mr-5">
        <SIMPLButtonCustomized
          label={t("Cancel")}
          secondary
          buttonHandlerAction={() => setCopyModal(false)}
        />
      </div>
      <div className="grow-0 basis-32">
        <SIMPLButtonCustomized
          label={t("Copy")}
          primary
          firstIcon={<FileCopyIcon fontSize="small" />}
          buttonHandlerAction={handleCopyVmTemplate}
        />
      </div>
    </>
  );
  /* Renders "Cancel" and "Delete Item" buttons for alerts */
  const renderAlertButtons = () => (
    <>
      <div className="grow-0 basis-32 mr-5">
        <SIMPLButtonCustomized
          label={t("Cancel")}
          secondary
          buttonHandlerAction={() => setDeleteModal(false)}
        />
      </div>
      <div className="grow-0 basis-32">
        <SIMPLButtonCustomized
          label={t("DeleteItem")}
          danger
          buttonHandlerAction={handleDeleteVmTemplate}
        />
      </div>
    </>
  );

  /*Renders delete alert with cancel and delete actions */
  const renderDeleteAlert = () =>
    deleteModal && (
      <SIMPLAlert
        onCancel={() => setDeleteModal(false)}
        message={t('MessageDialogDelete', { item: item.name })}
        title={t("ExcludeVmTemplate")}
        actions={renderAlertButtons()}
      />
    );
  const handleConfirmDelete = (template: TemplateResponse) => {
    if (template.id) {
      setItem(template);
      setDeleteModal(true);
    } else {
      console.warn(LABELS.TemplateDoesNotExist);
    }
  };
  const handleConfirmCopy = (template: TemplateResponse) => {
    if (template.id) {
      setItem(template);
      setCopyModal(true);
    } else {
      console.warn(LABELS.TemplateDoesNotExist);
    }
  };

  const handleCopyVmTemplate = () =>
    handleCopyEntity({
      copyFn: (id) => copyVmTemplate(id).unwrap(),
      id: item.id,
      refetchFn: refetch,
      onBeforeCopy: () => setCopyModal(false),
      onSuccess: (title, message) => {
        setResponseDetailsVmTemplate({ title, message });
        setCopyModalVmError(false);
        setCopyModalVmSuccess(true);
      },
      onError: (title, message) => {
        setResponseDetailsVmTemplate({ title, message });
        setCopyModalVmSuccess(false);
        setCopyModalVmError(true);
      },
      t
    });
  const handleDeleteVmTemplate = async () => {
    handleDeleteEntity({
      deleteFn: () => deleteVmTemplate(item.id).unwrap(),
      id: item.id,
      refetchFn: refetch,
      onBeforeDelete: () => setDeleteModal(false),
      onSuccess: (title, message) => {
        setResponseDetailsVmTemplate({ title, message });
        setCopyModalVmSuccess(true);
        setCopyModalVmError(false);
      },
      onError: (title, message) => {
        setResponseDetailsVmTemplate({ title, message });
        setCopyModalVmError(true);
        setCopyModalVmSuccess(false);
      },
      t
    });
  };

  const showFilterHandler = () => setShowFilter((prev) => !prev);

  const handleSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSize(Number(e.target.value));
  };
  const handleResetAll = () => {
    setPage(0);
    setShowFilter(false);
  };
  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handleEdit = (scriptItem: TemplateResponse) => {
    if (!scriptItem.id) {
      console.warn(LABELS.ScriptIdDonotExist);
      return;
    }

    setItem(scriptItem);
    navigate("/vm-template/edit-vm-template", {
      state: { editedItem: scriptItem },
    });
  };
  /* Renders the "Add Script" button */
  const renderButtonAddProvider = () => (
    <SIMPLButtonCustomized
      label={t("AddVmTemplate")}
      buttonHandlerAction={handleAddVmTemplate}
      secondIcon={<AddIcon fontSize="small" />}
      primary
    />
  );
  const handleAddVmTemplate = () => {
    navigate("/vm-template/add-vm-template");
  };

  const handleOpenDetail = (scriptItem: TemplateResponse) => {
    setItem(scriptItem);
    setViewModalVmTemplate(true);
  };
  const renderDetailViewVmTemplate = () =>
    viewModalVmTemplate && (
      <DetailsVmTemplate
        onClose={() => setViewModalVmTemplate(false)}
        title={t("DetailView")}
        item={item}
      />
    );
  const renderVMAlertDialog = () => (
    <Alerts
      showSuccess={copyModalVmSuccess}
      showError={copyModalVmError}
      title={responseDetailsVmTemplate?.title}
      successMessage={responseDetailsVmTemplate?.message || ""}
      errorMessage={responseDetailsVmTemplate?.message || ""}
      onCloseSucessDialog={() => setCopyModalVmSuccess(false)}
      onCloseErrorDialog={() => setCopyModalVmError(false)}
      enabledSuccess={copyModalVmSuccess}
      enabledError={copyModalVmError}
    />
  );
  const renderSIMPLTable = () => (
    <SIMPLTable
      title={t("VMTemplateManagment")}
      clear={handleResetAll}
      button={renderButtonAddProvider()}
      page={page}
      size={size}
      showPagination={showPagination}
      active={true}
      showHandler={showFilterHandler}
      filterActive={showFilter}
      tableHeaders={VmTemplateHeader}
      rows={rows}
      onChangePage={handlePageChange}
      onChange={handleSizeChange}
      handleOpenEdit={handleEdit}
      handleOpenDetail={handleOpenDetail}
      handleDelete={handleConfirmDelete}
      handleOpenCopy={handleConfirmCopy}
      defaultSortKey="creationDate_raw"
      sortConfig={sortConfig}
      handleSort={handleSort}
      chipLabels={[]}
      custumizedNoDataAvailable={LABELS.NoTemplatesAvailable}
      showButtonDecommissionInModal={false}
      showConfigFileInModal={false}
      showButtonDownloadInModal={false}
      showReviewOutputTemplateInModal={false}
      showButtonMoreActions
      handlerChips={() => {}}
      lastBorder="border-l border-white"
      setIsActive={setIsActive}
      isActive={isActive}
       activeLabel={t("Active")  }  
      inactiveLabel={t("Inactive")}
      tabs
    />
  );

  return (
    <SIMPLMenuHeader>
      {renderVMAlertDialog()}
      <Layout>
        {renderDeleteAlert()}
        {renderCopyAlert()}
        {renderDetailViewVmTemplate()}
        <LoadingWrapper
          loadingTitle={t("LoadingVmTemplates")}
          isLoading={isLoading || isFetching}
          isError={isError}
        >
          {renderSIMPLTable()}
        </LoadingWrapper>
      </Layout>
    </SIMPLMenuHeader>
  );
};
